import {
  type RefCallback,
  type RefObject,
  useEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { CenterPlaybackButton } from "./CenterPlaybackButton";
import { ExactVideoOverlay } from "./ExactVideoOverlay";
import type { VideoJsPlayer } from "../hooks/usePlayer";

const ASSET_VIEW_ZOOM_STEP = 0.1;
const videoStyle = "h-full w-full focus:outline-none outline-none outline-0 ring-0";

export interface VideoSurfaceProps {
  /** Attached to the inner data-vjs-player div. Comes from usePlayer. */
  videoRef: RefCallback<HTMLDivElement>;
  /** The underlying <video-js> custom element. Portal target for overlays + listener target for click/dblclick + background-color sink. */
  videoElementNode: HTMLElement | null;
  /** For reading the inner <video> element's dimensions in the overlay-sizing effect. */
  playerRef: RefObject<VideoJsPlayer | null>;
  /** Pinged when the JKL ramp flash should fire. */
  centerButtonPing: boolean;
  /** Read inside the flash overlay to render the current rate. */
  tempPlaybackRateRef: RefObject<number>;
  /** White vs black backdrop behind letterboxed video. */
  videoBackgroundWhite: boolean;
  /** Disables react-zoom-pan-pinch. */
  isSmallScreen: boolean;
  /** Also disables zoom (no panning on fullscreen). */
  isFullScreen: boolean;
  /** Explicit override. */
  disableZoom?: boolean;
  /** Fired when the video surface is clicked. Parent decides what to do (typically togglePlay, but skipped if click is on a control). */
  onVideoClick: (event: MouseEvent) => void;
  /** Fired on double-click. Parent typically toggles fullscreen. */
  onVideoDblClick: (event: MouseEvent) => void;
}

/**
 * The video surface itself: zoom/pan wrapper, the data-vjs-player div, and
 * the portaled overlays (center playback flash + aspect-ratio guide).
 *
 * Owns three effects keyed on `videoElementNode`:
 *  1. click/dblclick listeners on the underlying <video-js> element
 *  2. background-color toggle
 *  3. aspect-ratio guide overlay sizing (positions ExactVideoOverlay over
 *     the *displayed* video area, accounting for letterboxing)
 *
 * All three only run once the player is initialized and the <video-js>
 * element is mounted.
 */
export function VideoSurface({
  videoRef,
  videoElementNode,
  playerRef,
  centerButtonPing,
  tempPlaybackRateRef,
  videoBackgroundWhite,
  isSmallScreen,
  isFullScreen,
  disableZoom,
  onVideoClick,
  onVideoDblClick,
}: VideoSurfaceProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Click + dblclick on the video element. Parent decides whether to act on
  // them (orchestrator skips toggle if the click landed on a control).
  useEffect(() => {
    if (!videoElementNode) return;
    videoElementNode.addEventListener("click", onVideoClick);
    videoElementNode.addEventListener("dblclick", onVideoDblClick);
    return () => {
      videoElementNode.removeEventListener("click", onVideoClick);
      videoElementNode.removeEventListener("dblclick", onVideoDblClick);
    };
  }, [videoElementNode, onVideoClick, onVideoDblClick]);

  // Background color (letterbox backdrop).
  useEffect(() => {
    if (!videoElementNode) return;
    videoElementNode.style.backgroundColor = videoBackgroundWhite
      ? "#ffffff"
      : "#000000";
  }, [videoElementNode, videoBackgroundWhite]);

  // Aspect-ratio guide overlay sizing — positions overlayRef over the
  // *displayed* video area (letterboxed inside the container).
  useEffect(() => {
    const playerInst = playerRef.current;
    if (!playerInst || !videoElementNode) return;
    const updateOverlay = () => {
      const innerVideo = playerInst
        .el()
        .querySelector("video") as HTMLVideoElement | null;
      if (!innerVideo || !overlayRef.current) return;
      const { clientWidth: cw, clientHeight: ch, videoWidth: vw, videoHeight: vh } =
        innerVideo;
      if (!vw || !vh) return;
      const scale = Math.min(cw / vw, ch / vh);
      const sw = vw * scale;
      const sh = vh * scale;
      overlayRef.current.style.width = `${sw}px`;
      overlayRef.current.style.height = `${sh}px`;
      overlayRef.current.style.transform = `translate3d(${(cw - sw) / 2}px, ${(ch - sh) / 2}px, 0)`;
      overlayRef.current.style.display = "block";
    };
    const resizeObserver = new ResizeObserver(updateOverlay);
    resizeObserver.observe(videoElementNode);
    playerInst.on("loadedmetadata", updateOverlay);
    updateOverlay();
    return () => resizeObserver.disconnect();
  }, [videoElementNode, playerRef]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <TransformWrapper
        disabled={isSmallScreen || isFullScreen || disableZoom}
        doubleClick={{ disabled: true }}
        // Disable ALL panning. The legacy config left `wheelPanning: true`,
        // which made scrolling over the video translate the content (the
        // -100px, -100px transform you'd see in devtools after any scroll).
        // We don't actually want zoom/pan on this player — keeping
        // TransformWrapper only because the data-vjs-player markup is
        // wrapped in it.
        panning={{ disabled: true }}
        initialScale={1}
        wheel={{ wheelDisabled: true, smoothStep: ASSET_VIEW_ZOOM_STEP }}
      >
        <TransformComponent>
          <div data-vjs-player className={videoStyle} ref={videoRef} />
        </TransformComponent>
      </TransformWrapper>

      {videoElementNode &&
        createPortal(
          <>
            <CenterPlaybackButton
              tempPlaybackRateRef={tempPlaybackRateRef}
              ping={centerButtonPing}
            />
            <ExactVideoOverlay ref={overlayRef} />
          </>,
          videoElementNode,
        )}
    </div>
  );
}
